import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Briefcase, Users, FileText, Building2, Pencil } from "lucide-react";
import { PortalLayout } from "../../components/PortalLayout";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";

export default function EmployerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [companyForm, setCompanyForm] = useState({});

  const load = useCallback(() => {
    api.get("/employer/dashboard").then((r) => {
      setData(r.data);
      setCompanyForm(r.data.company || {});
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCompany = async () => {
    try {
      const { company_name, trading_as, industry, office_location, contact_person, website, about, hiring_contact_phone } = companyForm;
      await api.put("/employer/company", { company_name, trading_as, industry, office_location, contact_person, website, about, hiring_contact_phone });
      toast.success("Company profile updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data)
    return <PortalLayout><div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-[#0d2f4d] border-t-transparent animate-spin" /></div></PortalLayout>;

  const { company, roles, documents, shortlist_count, account, membership_usage, consents } = data;

  return (
    <PortalLayout>
      <div data-testid="employer-dashboard">
        <p className="text-xs uppercase tracking-[0.25em] text-[#7C8514] font-medium">Employer dashboard</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Welcome, {user?.first_name}</h1>
        <p className="text-sm text-[#51637a] mt-1">{company?.company_name} · {account?.title}
          {company?.subscription?.status === "active" && (
            <span className="ml-2 inline-flex rounded-full bg-[#C1CC24] text-[#0d2f4d] text-xs font-semibold px-3 py-0.5" data-testid="partner-badge">
              {company.subscription.plan_name} partner
            </span>
          )}
        </p>

        {membership_usage && (
          <div className="ditch-card p-6 mt-8" data-testid="membership-usage-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-heading font-bold text-[#0d2f4d]">Membership usage</h2>
                <p className="text-sm text-[#51637a] mt-1" data-testid="membership-usage-text">
                  {membership_usage.placements_used} of {membership_usage.placements_limit} placements used this year · {membership_usage.plan_name}
                </p>
              </div>
              <span className="text-xs text-[#51637a]">
                {membership_usage.placements_used >= membership_usage.placements_limit
                  ? "Additional placements available at your exclusive member rate."
                  : `${membership_usage.placements_limit - membership_usage.placements_used} remaining`}
              </span>
            </div>
            <div className="mt-4 h-2.5 rounded-full bg-[#f5f3ee] overflow-hidden">
              <div className="h-full rounded-full bg-[#C1CC24] transition-all" data-testid="membership-usage-bar"
                style={{ width: `${Math.min(100, (membership_usage.placements_used / membership_usage.placements_limit) * 100)}%` }} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {[
            { icon: Briefcase, label: "Active role requests", value: roles.length, testid: "stat-roles" },
            { icon: Users, label: "Shortlisted candidates", value: shortlist_count, testid: "stat-shortlist" },
            { icon: FileText, label: "Documents uploaded", value: documents.length, testid: "stat-documents" },
          ].map(({ icon: Icon, label, value, testid }) => (
            <div key={label} className="ditch-card p-6" data-testid={testid}>
              <Icon size={20} className="text-[#7C8514]" />
              <p className="font-heading text-3xl font-extrabold text-[#0d2f4d] mt-3">{value}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-[#51637a] mt-1">{label}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mt-4">
          <div className="ditch-card p-6 lg:col-span-2" data-testid="roles-card">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-[#0d2f4d]">Role requests</h2>
              <Link to="/employer/submit-role" data-testid="dashboard-submit-role-cta"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#C1CC24] text-[#0d2f4d] text-xs font-medium px-4 py-2 hover:bg-[#aeb81f] transition-colors">
                Submit new role <ArrowRight size={14} />
              </Link>
            </div>
            {roles.length === 0 ? (
              <div className="mt-6 text-center py-8 border border-dashed border-[#e3e0d3] rounded-lg" data-testid="roles-empty-state">
                <Briefcase className="mx-auto text-[#e3e0d3]" size={28} />
                <p className="text-sm text-[#51637a] mt-3">No active roles yet. Submit your first role and we'll start sourcing.</p>
              </div>
            ) : (
              <ul className="mt-5 space-y-3">
                {roles.map((r) => (
                  <li key={r.id} className="flex items-center justify-between border border-[#e3e0d3] rounded-lg px-4 py-3.5" data-testid={`role-item-${r.id}`}>
                    <div>
                      <p className="font-medium text-[#0d2f4d] text-sm">{r.title}</p>
                      <p className="text-xs text-[#51637a] mt-0.5">{r.employment_type} · {r.location} · {r.salary_range}</p>
                    </div>
                    <span className="rounded-full bg-[#f5f3ee] text-[#0d2f4d] text-xs font-medium px-3 py-1 capitalize">{r.status.replace("_", " ")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="ditch-card p-6" data-testid="company-card">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-[#0d2f4d] flex items-center gap-2"><Building2 size={17} /> Company</h2>
              <button onClick={() => setEditing(!editing)} data-testid="edit-company-button" className="text-[#51637a] hover:text-[#7C8514] transition-colors"><Pencil size={15} /></button>
            </div>
            {!editing ? (
              <div className="mt-4 space-y-2 text-sm">
                <p className="font-medium text-[#0d2f4d]">{company?.company_name}</p>
                {company?.trading_as && <p className="text-[#51637a]">Trading as: {company.trading_as}</p>}
                <p className="text-[#51637a]">{company?.industry || "Industry not set"}</p>
                <p className="text-[#51637a]">{company?.office_location || company?.location || "Office location not set"}</p>
                <p className="text-[#51637a]">{company?.contact_person ? `Contact: ${company.contact_person}` : "Contact person not set"}</p>
                <p className="text-[#51637a]">{company?.hiring_contact_phone || "Phone not set"}</p>
                <p className="text-[#51637a]">{company?.website || "Website not set"}</p>
                {company?.about && <p className="text-[#51637a] text-xs leading-relaxed mt-2">{company.about}</p>}
                <div className="border-t border-[#e3e0d3] pt-3 mt-3" data-testid="company-terms-block">
                  <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]">Terms</p>
                  <p className="mt-1.5 text-[#0d2f4d]">
                    {consents?.some((c) => c.document_name === "membership_pack")
                      ? "✓ Membership Pack signed" : "Membership Pack not signed yet"}
                  </p>
                  {company?.recruitment_fee && <p className="text-[#51637a]">Recruitment fee: {company.recruitment_fee}</p>}
                  {company?.invoice_terms && <p className="text-[#51637a]">Invoice terms: {company.invoice_terms}</p>}
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {[["company_name", "Agency / company name"], ["trading_as", "Trading as"], ["industry", "Industry"],
                  ["office_location", "Office location"], ["contact_person", "Contact person"],
                  ["hiring_contact_phone", "Phone"], ["website", "Website"]].map(([k, l]) => (
                  <div key={k}><label className={labelCls}>{l}</label>
                    <input value={companyForm[k] || ""} onChange={(e) => setCompanyForm({ ...companyForm, [k]: e.target.value })} data-testid={`company-${k}`} className={inputCls} /></div>
                ))}
                <div><label className={labelCls}>About</label>
                  <textarea rows={3} value={companyForm.about || ""} onChange={(e) => setCompanyForm({ ...companyForm, about: e.target.value })} data-testid="company-about" className={inputCls} /></div>
                <button onClick={saveCompany} data-testid="save-company-button"
                  className="w-full rounded-full bg-[#0d2f4d] text-white text-sm font-medium py-2.5 hover:bg-[#1c4d78] transition-colors">Save company</button>
              </div>
            )}
          </div>
        </div>

        <div className="ditch-card p-6 mt-4" data-testid="shortlist-summary-card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-bold text-[#0d2f4d]">Shortlisted candidates</h2>
              <p className="text-sm text-[#51637a] mt-1">
                {shortlist_count > 0
                  ? `${shortlist_count} candidate${shortlist_count === 1 ? "" : "s"} shared with you by our recruiters.`
                  : "Your recruiter will share candidates here once vetting is complete."}
              </p>
            </div>
            <Link to="/employer/shortlist" data-testid="view-shortlist-cta"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#0d2f4d] text-[#0d2f4d] text-xs font-medium px-4 py-2 hover:bg-[#0d2f4d] hover:text-white transition-colors">
              View shortlist <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
